import type { IncomeSplitConfig } from '@fruitful/income-split';

import { requestBuilder } from '../../../http/RequestBuilder';
import type { IncomeSplitExecutionTransaction } from '../../entityTypes';

interface SimulateIncomeSplitBody {
    syncteraAccountId: string;
    amountCents: number;
}

/**
 * Passive diagnostic emitted by the backend income-split simulator. It detects
 * routing rules that lost their original money-map rule id (`originalId`), which
 * makes contribution-cap tracking undercount and can let a member over-contribute.
 * It never changes plan/transfer output — it only decides how the simulation
 * reports pass/fail. Absent means the audit didn't run (treat as unknown/pass).
 */
export interface SimulatorLineageFinding {
    // true = this member's rules lost originalId lineage → caps are latently
    // undercounting (the bug)
    hasLostLineage: boolean;
    // true = they have ALREADY over-contributed as a result (concrete harm)
    hasRealizedOverContribution: boolean;
    // the income-split rule ids implicated
    affectedRuleIds: string[];
    // sum of prior contributions that got "orphaned" (no longer counted toward
    // the yearly cap), in integer cents
    totalOrphanedYearlyCents: number;
    // sum actually over-contributed beyond cap, in integer cents
    totalRealizedOverContributionCents: number;
    // months where a realized over-contribution happened, e.g. ["2026-10"]
    overContributionMonths: string[];
}

export type SimVerdict = 'pass' | 'warn' | 'fail';

/**
 * Maps a lineage finding to a pass/warn/fail verdict:
 * - absent → pass (audit didn't run / unknown; don't block)
 * - realized over-contribution → fail (concrete harm)
 * - lost lineage only → warn (latent cap undercount)
 * - otherwise → pass (clean)
 */
export const lineageVerdict = (
    finding?: SimulatorLineageFinding | null,
): SimVerdict => {
    if (finding == null) {
        return 'pass';
    }
    if (finding.hasRealizedOverContribution) {
        return 'fail';
    }
    if (finding.hasLostLineage) {
        return 'warn';
    }
    return 'pass';
};

export interface SimulateBulkIncomeSplitBody {
    limit?: number;
    membershipIds?: string[];
}

export interface TransferInfo {
    type: string;
    amountToTransfer: number;
    sourceAccountId: string;
    destinationAccountId: string;
    memo?: string | null;
}

export interface DestinationAccountDetails {
    nickname: string;
    accountNumber: string;
    accountNumberMasked: string;
    syncteraAccountId?: string;
    status?: string;
}

export interface DestinationInternalInvestmentAccountDetails {
    nickname: string;
    accountNumber: string;
    accountNumberMasked: string;
    apexAccountId: string;
    state: string;
}

export interface DestinationDepositoryAccountDetails {
    nickname: string;
    accountNumber: string;
    accountNumberMasked: string;
    syncteraAccountId: string;
    status: string;
}

export interface LimitDetails {
    yearlyMet?: boolean;
    monthlyMet?: boolean;
    hasReachedCapacity: boolean;
    remainingMonthlyCapacity?: number;
    remainingContributionAllowedMet?: boolean;
    remainingContributionAllowed?: number | null;
}

export interface OriginalRule {
    id: string;
    originalId: string | null;
    incomeSplitRuleSetId: string;
    accountId: string;
    type: string;
    priority: number;
    moneyMapRuleId: string | null;
    accountType: string;
    targetBalanceCents: number | null;
    percentage: string;
    monthlyContributionMaxCents: number | null;
    yearlyContributionMaxCents: number | null;
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface RulePlan {
    id: string;
    originalId: string | null;
    accountId: string;
    type: string;
    priority: number;
    accountType: string;
    percentage: number;
    destinationAccountDetails?: DestinationAccountDetails;
    destinationInternalInvestmentAccountDetails?: DestinationInternalInvestmentAccountDetails;
    destinationDepositoryAccountDetails?: DestinationDepositoryAccountDetails;
    transferInfo: TransferInfo;
    limitDetails?: LimitDetails;
    originalRule?: OriginalRule;
    allocatedAmountDetails?: {
        desiredAmount: number;
        actualAmount: number;
    };
}

export interface RuleSetPlan {
    ruleSetId: string;
    ruleSetName: string;
    priority: number;
    percentage: number;
    remainingFunds: number;
    rules: RulePlan[];
    amountForRuleSetSplit: number;
    ruleSetReached: boolean;
}

export interface RuleValidationDetail {
    ruleId: string;
    isValid: boolean;
    reason?: string;
}

export interface IncomeSplitPlanResponse {
    incomeSplitExecutionId: string;
    incomeSplitConfigId: string;
    transaction: {
        uuid: string;
        amount: number;
        description: string;
        transactionTime: string;
        postedDate?: string;
        type: string;
        subtype: string;
        status: string;
    };
    postedTransaction: {
        id: number;
        uuid: string;
        type: string;
        subtype: string;
        status: string;
        created: string;
        effective_date: string;
        posted_date: string;
    };
    sourceAccount: {
        syncteraAccountId: string;
        bankAccountId: string;
        nickname: string;
        accountNumber: string;
        accountNumberMasked: string;
    };
    ruleSetPlans: RuleSetPlan[];
    totalIncomeAmount: number;
    totalAllocatedAmount: number;
    finalRemainingFunds: number;
    ruleValidationDetails: RuleValidationDetail[];
}

export interface BulkSimulationResultItem {
    membershipId: string;
    originalExecutionId: string;
    sourceSyncteraAccountId: string;
    amountCents: number;
    status: 'success' | 'error';
    simulationResult: IncomeSplitPlanResponse | null;
    incomeSplitConfig: IncomeSplitConfig | null;
    membership: {
        id: string;
        users: Array<{
            id: string;
            firstName?: string | null;
            lastName?: string | null;
            email?: string | null;
        }>;
    } | null;
    ytdTransactionsByRuleId: Record<string, IncomeSplitExecutionTransaction[]>;
    validationErrors: string[];
    error?: string;
    // Optional lineage diagnostic for this row. Absent = audit didn't run.
    lineageAudit?: SimulatorLineageFinding;
}

export interface BulkSimulationResponse {
    totalRequested: number;
    totalSimulated: number;
    totalSucceeded: number;
    totalFailed: number;
    totalWithValidationErrors: number;
    results: BulkSimulationResultItem[];
}

export interface SimulateIncomeSplitResponse {
    plan: IncomeSplitPlanResponse;
    config: IncomeSplitConfig;
    membership: {
        id: string;
        users: Array<{
            id: string;
            firstName?: string | null;
            lastName?: string | null;
            email?: string | null;
        }>;
    };
    ytdTransactionsByRuleId?: Record<string, IncomeSplitExecutionTransaction[]>;
    // Optional top-level lineage diagnostic. Absent = audit didn't run.
    lineageAudit?: SimulatorLineageFinding;
}

const useSimulateIncomeSplit = requestBuilder.mutation<
    SimulateIncomeSplitResponse | null,
    SimulateIncomeSplitBody
>({
    endpoint: '/api/admin/money-map/income-split/simulate',
    method: 'POST',
});

export const useSimulateBulkIncomeSplit = requestBuilder.mutation<
    BulkSimulationResponse,
    SimulateBulkIncomeSplitBody
>({
    endpoint: '/api/admin/money-map/income-split/simulate-bulk',
    method: 'POST',
});

/**
 * One affected member returned by the admin lineage-audit sweep. The exact
 * backend shape is not pinned down here, so we keep the finding fields inline
 * (a flattened `SimulatorLineageFinding`) plus the identifying ids, and the UI
 * renders defensively (with a raw-JSON fallback) in case the shape differs.
 */
export interface LineageAuditSweepMember extends Partial<SimulatorLineageFinding> {
    membershipId?: string;
    userId?: string;
    // Some backends nest the finding instead of flattening it; support both.
    finding?: SimulatorLineageFinding;
    lineageAudit?: SimulatorLineageFinding;
}

export interface LineageAuditSweepSummary {
    scannedConfigs: number;
    affectedMemberships: number;
    realizedMemberships: number;
    latentMemberships: number;
    overContributedMemberships: number;
    affectedRules: number;
    totalOrphanedYearlyCents: number;
    totalRealizedOverContributionCents: number;
    // Keyed by "YYYY-MM"; value shapes aren't pinned down, so stay loose.
    overContributionByMonth: Record<string, unknown>;
    membershipsWithOverContributionByMonth: Record<string, unknown>;
}

export interface LineageAuditSweepResponse {
    year: number;
    summary: LineageAuditSweepSummary;
    affected?: LineageAuditSweepMember[];
}

export interface LineageAuditSweepParams {
    membershipIds?: string[];
    limit?: number;
    // "YYYY-MM"
    affectedInMonth?: string;
}

const buildLineageAuditSweepEndpoint = (
    params?: LineageAuditSweepParams,
): string => {
    const query = new URLSearchParams();

    if (params?.membershipIds != null && params.membershipIds.length > 0) {
        query.set('membershipIds', params.membershipIds.join(','));
    }
    if (params?.limit != null) {
        query.set('limit', String(params.limit));
    }
    if (params?.affectedInMonth != null && params.affectedInMonth !== '') {
        query.set('affectedInMonth', params.affectedInMonth);
    }

    const queryString = query.toString();
    const base = '/api/admin/money-map/income-split/lineage-audit';
    return queryString !== '' ? `${base}?${queryString}` : base;
};

export const useIncomeSplitLineageAuditSweep = requestBuilder.query<
    LineageAuditSweepResponse,
    LineageAuditSweepParams
>({
    endpoint: buildLineageAuditSweepEndpoint,
    queryKey: 'incomeSplitLineageAuditSweep',
});

export default useSimulateIncomeSplit;
